import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SqlAgentApiService, { SqlAgentAskResponse, ExampleQuery } from '../services/sqlAgentApi';
import { formatToEDT } from '../utils/timeZone';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  sqlQuery?: string;
  results?: any[];
  error?: string;
  metadata?: {
    execution_time_ms?: number;
    row_count?: number;
    tables_used?: string[];
  };
  fullResponse?: SqlAgentAskResponse; // Store the complete response
}

function SqlResultsDisplay({ data, sql, rowCount }: { data: any[]; sql?: string; rowCount?: number }) {
  if (!data || data.length === 0) {
    return null;
  }

  // Get all unique columns from all rows (in case rows have different properties)
  const allColumns = new Set<string>();
  data.forEach(row => {
    Object.keys(row).forEach(key => allColumns.add(key));
  });
  const columns = Array.from(allColumns);
  
  // Function to format cell values based on their type
  const formatCellValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'boolean') {
      return value ? '✓' : '✗';
    }
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return `[${value.length} items]`;
      }
      if (value instanceof Date) {
        return value.toLocaleString();
      }
      // For objects, show a preview or JSON
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return '{}';
      }
      // Show first few key-value pairs
      const preview = keys.slice(0, 2).map(k => `${k}: ${value[k]}`).join(', ');
      return `{${preview}${keys.length > 2 ? ', ...' : ''}}`;
    }
    return String(value);
  };
  
  return (
    <div className="mt-4 w-full overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {columns.map(col => (
                <th key={col} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {data.slice(0, 100).map((row, i) => (
              <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                {columns.map(col => {
                  const value = row[col];
                  const isObject = value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
                  
                  return (
                    <td key={col} className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {isObject ? (
                        <details className="cursor-pointer">
                          <summary className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                            {formatCellValue(value)}
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs overflow-x-auto">
                            {JSON.stringify(value, null, 2)}
                          </pre>
                        </details>
                      ) : (
                        <div className="max-w-xs truncate" title={formatCellValue(value)}>
                          {formatCellValue(value)}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400">
        {data.length > 100 ? `Showing first 100 of ${rowCount || data.length} rows` : `Total rows: ${rowCount || data.length}`}
      </div>
    </div>
  );
}

function FullResponseDisplay({ response }: { response: SqlAgentAskResponse }) {
  // Handle both "results" and "rows" fields
  const data = response.results || response.rows || [];
  const hasResults = data.length > 0;
  // Check for row_count in multiple places
  const rowCount = response.row_count || response.metadata?.row_count || data.length || 0;
  // Check for execution_time_ms in multiple places
  const executionTime = response.execution_time_ms || response.metadata?.execution_time_ms;
  
  return (
    <div className="space-y-4">
      {/* Header Section with Intent and Row Count */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Intent Badge */}
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
            response.sql_query?.toLowerCase().includes('select') ? 'bg-blue-100 text-blue-800' :
            response.sql_query?.toLowerCase().includes('insert') ? 'bg-green-100 text-green-800' :
            response.sql_query?.toLowerCase().includes('update') ? 'bg-yellow-100 text-yellow-800' :
            response.sql_query?.toLowerCase().includes('delete') ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {response.sql_query?.split(' ')[0]?.toUpperCase() || 'QUERY'}
          </span>
          
          {/* Row Count */}
          <span className="text-lg font-semibold text-gray-900">
            {rowCount} row{rowCount !== 1 ? 's' : ''}
          </span>
        </div>
        
        {/* Execution Time */}
        {executionTime && (
          <span className="text-sm text-gray-500">
            ⚡ {executionTime}ms
          </span>
        )}
      </div>

      {/* SQL Query Section */}
      {response.sql_query && (
        <div className="bg-gray-900 dark:bg-gray-800 rounded-lg p-4 overflow-x-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">SQL Query</span>
            <button
              onClick={() => navigator.clipboard.writeText(response.sql_query || '')}
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-white dark:hover:text-gray-200 transition-colors"
            >
              Copy
            </button>
          </div>
          <pre className="text-sm text-gray-100 dark:text-gray-200 font-mono">
            <code>{response.sql_query}</code>
          </pre>
        </div>
      )}

      {/* Tables Used */}
      {response.metadata?.tables_used && response.metadata.tables_used.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium">Tables used:</span> {response.metadata.tables_used.join(', ')}
          </p>
        </div>
      )}

      {/* Warnings */}
      {response.warnings && response.warnings.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <p className="text-sm text-amber-800 dark:text-amber-400 font-medium mb-1">⚠️ Warnings:</p>
          <ul className="list-disc list-inside text-sm text-amber-700 dark:text-amber-400">
            {response.warnings.map((warning, i) => (
              <li key={i}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Explanation */}
      {response.explanation && (
        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-900 dark:text-blue-300">{response.explanation}</p>
        </div>
      )}

      {/* Results Table */}
      {hasResults && (
        <SqlResultsDisplay 
          data={data} 
          rowCount={rowCount}
        />
      )}

      {/* No Results Message */}
      {!hasResults && (response.results !== undefined || response.rows !== undefined) && (
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-gray-600 dark:text-gray-400">No data found for your query.</p>
        </div>
      )}

      {/* Error Display */}
      {response.error && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-400">
            <span className="font-medium">Error:</span> {response.error}
          </p>
        </div>
      )}
    </div>
  );
}

export function SeriesDataAgent() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: "Hello! I'm your Series Data Agent. I can help you explore your database using natural language. Ask me about users, conversations, messages, or any other data you'd like to analyze.",
      role: 'assistant',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [examples, setExamples] = useState<ExampleQuery[]>([]);
  const [isHealthy, setIsHealthy] = useState(true);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    // Scroll within the message container, not the entire page
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);



  // Fetch example queries
  useEffect(() => {
    const fetchExamples = async () => {
      try {
        const exampleQueries = await SqlAgentApiService.getExampleQueries();
        setExamples(exampleQueries);
      } catch (error) {
        console.error('Failed to fetch examples:', error);
      }
    };

    fetchExamples();
  }, []);

  // Check SQL Agent health
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const health = await SqlAgentApiService.checkHealth();
        setIsHealthy(health.status === 'healthy' || health.status === 'ok');
      } catch (error) {
        setIsHealthy(false);
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading || !isHealthy) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: input,
      role: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Call SQL Agent API
      const response = await SqlAgentApiService.askQuestion({
        question: input,
        include_schema: true,
        limit: 1000,  // Increased from default 100
        execute: true  // Ensure query is executed, not just generated
      });
      
      // Debug logging
      console.log('SQL Agent Response:', {
        hasResults: !!response.results,
        resultCount: response.results?.length || 0,
        rowCount: response.metadata?.row_count,
        hasError: !!response.error,
        hasSqlQuery: !!response.sql_query,
        fullResponse: response
      });

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: '', // Content not needed when we have fullResponse
        role: 'assistant',
        timestamp: new Date(),
        sqlQuery: response.sql_query,
        results: response.results || response.rows, // Handle both "results" and "rows"
        metadata: {
          row_count: response.metadata?.row_count || response.row_count,
          execution_time_ms: response.metadata?.execution_time_ms || response.execution_time_ms,
          tables_used: response.metadata?.tables_used
        },
        error: response.error,
        fullResponse: response // Store the complete response
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: `I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again or rephrase your question.`,
        role: 'assistant',
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };



  const handleExampleClick = (example: ExampleQuery) => {
    setInput(example.description);
  };

  return (
    <div className="flex h-full flex-col relative">
      {/* Header */}
      <div className="px-8 pt-8 pb-6 bg-gradient-to-b from-white/70 via-white/50 to-transparent dark:from-gray-900/70 dark:via-gray-900/50 dark:to-transparent backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-['SF_Pro_Display',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] font-bold text-gray-900 dark:text-gray-100">
                  Series Data Agent
                </h1>
                <p className="text-gray-600 dark:text-gray-400 font-['SF_Pro_Text',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif]">
                  Ask questions about your data in natural language
                </p>
              </div>
              
              {/* Health indicator */}
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${isHealthy ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  SQL Agent {isHealthy ? 'Connected' : 'Disconnected'}
                </span>
              </div>
            </div>
          </div>

          {/* Example queries */}
          {examples.length > 0 && (
            <div className="mt-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Try asking:</p>
              <div className="flex flex-wrap gap-2">
                {examples.slice(0, 4).map((example, i) => (
                  <button
                    key={i}
                    onClick={() => handleExampleClick(example)}
                    className="px-3 py-1.5 bg-white/60 dark:bg-gray-800/60 hover:bg-white/80 dark:hover:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    {example.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chat Messages */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-8 py-6 pb-28"
      >
        <div className="max-w-4xl mx-auto space-y-4">
          <AnimatePresence>
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[70%] ${message.role === 'user' ? '' : 'w-full'}`}>
                  {message.role === 'user' ? (
                    // User message - simple display
                    <div className="rounded-2xl px-4 py-3 bg-blue-500 dark:bg-blue-600 text-white">
                      <p className="font-['SF_Pro_Text',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif]">
                        {message.content}
                      </p>
                      <p className="text-xs mt-1 text-blue-100 dark:text-blue-200">
                        {message.timestamp.toLocaleTimeString(undefined, { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                      </p>
                    </div>
                  ) : (
                    // Assistant message
                    <>
                      {message.fullResponse ? (
                        // Use FullResponseDisplay for complete responses
                        <div className="w-full">
                          <FullResponseDisplay response={message.fullResponse} />
                          <p className="text-xs mt-2 text-gray-500">
                            {message.timestamp.toLocaleTimeString(undefined, { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                          </p>
                        </div>
                      ) : (
                        // Fallback to simple display for initial message or error messages
                        <div className="rounded-2xl px-4 py-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 text-gray-900 dark:text-gray-100">
                          <div className="font-['SF_Pro_Text',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] whitespace-pre-wrap">
                            {message.content}
                          </div>
                          <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                            {message.timestamp.toLocaleTimeString(undefined, { 
                          hour: 'numeric', 
                          minute: '2-digit', 
                          hour12: true 
                        })}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 rounded-2xl px-4 py-3">
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Form - Fixed at bottom */}
      <div className="fixed bottom-0 left-[229px] right-0 border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl px-8 py-4 z-30 shadow-lg">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="relative flex items-center">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isHealthy ? "Ask about your data..." : "SQL Agent is not available"}
              disabled={isLoading || !isHealthy}
              className="w-full px-4 py-3 pr-12 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all duration-200 font-['SF_Pro_Text',_-apple-system,_BlinkMacSystemFont,_'Segoe_UI',_Roboto,_'Helvetica_Neue',_Arial,_sans-serif] disabled:opacity-50 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading || !isHealthy}
              className="absolute right-2 w-8 h-8 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}